import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';
import { handleApiError, kanbanAPI } from '../utils/api';

const PRESET_COLORS = [
  '#6366f1', // Indigo
  '#8b5cf6', // Violet
  '#ec4899', // Pink
  '#ef4444', // Red
  '#f59e0b', // Amber
  '#10b981', // Emerald
  '#06b6d4', // Cyan
  '#3b82f6', // Blue
];

function ColumnModal({ isOpen, onClose, onSuccess, column }) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    color: '#6366f1'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (column) {
      setFormData({
        name: column.name || '',
        color: column.color || '#6366f1'
      });
    }
  }, [column]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (column) {
        await kanbanAPI.updateColumn(column.id, formData);
      } else {
        await kanbanAPI.createColumn(formData);
      }

      toast.success(column ? t('dashboard.kanban.columnModal.updateSuccess') : t('dashboard.kanban.columnModal.createSuccess'));
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(handleApiError(error, t('dashboard.kanban.columnModal.saveError')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-slate-950/55 backdrop-blur-sm"
              onClick={onClose}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-xl rounded-2xl border border-slate-200 bg-white shadow-2xl"
            >
              {/* Header */}
              <div className="border-b border-slate-200 bg-gradient-to-r from-blue-50 via-indigo-50 to-cyan-50 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">
                      {column ? t('dashboard.kanban.columnModal.editTitle') : t('dashboard.kanban.columnModal.createTitle')}
                    </h2>
                    <p className="mt-0.5 text-sm text-slate-600">
                      {t('dashboard.kanban.columnModal.subtitle', 'Configurez la colonne pour structurer votre pipeline de recrutement.')}
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                    aria-label={t('common.close')}
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Nom de la colonne */}
                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <label htmlFor="name" className="mb-1 block text-sm font-semibold text-slate-700">
                    {t('dashboard.kanban.columnModal.nameLabel')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder={t('dashboard.kanban.columnModal.namePlaceholder')}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-800 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>

                {/* Couleur */}
                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <label className="mb-2 block text-sm font-semibold text-slate-700">
                    {t('dashboard.kanban.columnModal.colorLabel')}
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {PRESET_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, color }))}
                        className={`h-10 w-full rounded-lg border-2 transition-all ${
                          formData.color === color
                            ? 'border-gray-900 scale-110'
                            : 'border-transparent hover:border-gray-300'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                  <div className="mt-3 flex items-center space-x-2">
                    <input
                      type="color"
                      id="color"
                      name="color"
                      value={formData.color}
                      onChange={handleChange}
                      className="h-10 w-20 rounded-md border border-slate-300"
                    />
                    <input
                      type="text"
                      value={formData.color}
                      onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                      placeholder="#6366f1"
                      className="flex-1 rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-800 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                </div>

                {/* Preview */}
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                  <label className="mb-1 block text-sm font-semibold text-slate-700">
                    {t('dashboard.kanban.columnModal.previewLabel')}
                  </label>
                  <div className="rounded-lg bg-white p-3 ring-1 ring-slate-200">
                    <div 
                      className="rounded-md border border-slate-200 p-3 shadow-sm"
                      style={{ borderLeft: `4px solid ${formData.color}` }}
                    >
                      <h3 className="font-medium text-slate-900">
                        {formData.name || t('dashboard.kanban.columnModal.previewNamePlaceholder')}
                      </h3>
                      <p className="mt-1 text-sm text-slate-500">
                        {t('dashboard.kanban.columnModal.previewDescription')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 border-t border-slate-200 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                  >
                    {t('dashboard.kanban.columnModal.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading ? t('dashboard.kanban.columnModal.saving') : (column ? t('dashboard.kanban.columnModal.update') : t('dashboard.kanban.columnModal.create'))}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}

export default ColumnModal;
